// Podglad montazu: znaczniki mocowan, "duch" klocka, zaznaczenie i cala
// konstrukcja w trybie budowania.
//
// Korzysta z tych samych funkcji rysujacych co symulacja, wiec "to, co widac
// w edytorze" jest tym, co widac w probie.

import { getDefinition } from '../domain/catalog';
import { rotateVec } from '../domain/geometry';
import type {
  PartDefinition,
  PartInstance,
  RobotBlueprint,
  Rotation,
  SocketKind,
  Vec2Like,
} from '../domain/types';
import { METERS_PER_GRID, degreesToRadians } from '../physics/units';
import type { Camera } from './camera';
import {
  drawPart,
  drawSocketHint,
  drawWheel,
  type PartPlacement,
  type PartStyle,
  type WheelPlacement,
} from './partArt';

export interface PreviewSocket {
  grid: Vec2Like;
  kind: SocketKind;
  normal: Vec2Like | null;
  compatible: boolean;
}

export interface PreviewGhost {
  definition: PartDefinition;
  gridX: number;
  gridY: number;
  rotation: Rotation;
  valid: boolean;
}

interface GridTransform {
  gridX: number;
  gridY: number;
  rotation: Rotation;
}

function placementOf(definition: PartDefinition, instance: GridTransform): PartPlacement {
  return {
    definition,
    originX: instance.gridX * METERS_PER_GRID,
    originY: instance.gridY * METERS_PER_GRID,
    angle: degreesToRadians(instance.rotation),
  };
}

function wheelPlacementOf(definition: PartDefinition, instance: GridTransform): WheelPlacement {
  const center = rotateVec({ x: 0.5, y: 0.5 }, instance.rotation);
  return {
    definition,
    centerX: (instance.gridX + center.x) * METERS_PER_GRID,
    centerY: (instance.gridY + center.y) * METERS_PER_GRID,
    angle: degreesToRadians(instance.rotation),
    radiusMeters: (definition.wheel?.radiusGrid ?? 0.5) * METERS_PER_GRID,
  };
}

/** Kola rysujemy inaczej niz korpus - z bieznikiem, piasta i szprychami. */
function drawInstance(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  definition: PartDefinition,
  instance: GridTransform,
  style: PartStyle = {},
): void {
  if (definition.wheel) {
    drawWheel(ctx, camera, wheelPlacementOf(definition, instance), style);
    return;
  }
  drawPart(ctx, camera, placementOf(definition, instance), style);
}

export function drawSockets(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  sockets: readonly PreviewSocket[],
): void {
  for (const socket of sockets) {
    drawSocketHint(
      ctx,
      camera,
      socket.grid,
      socket.normal,
      socket.compatible ? 'compatible' : 'free',
    );
  }
}

/** Polprzezroczysty podglad klocka: zielony gdy montaz poprawny, czerwony gdy nie. */
export function drawGhost(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  ghost: PreviewGhost,
): void {
  drawInstance(ctx, camera, ghost.definition, ghost, {
    fill: ghost.valid ? '#6bd18a' : '#ff8a7a',
    stroke: ghost.valid ? '#2f7d4a' : '#a33b2c',
    alpha: 0.6,
  });
}

/** Wyroznienie zaznaczonego klocka. */
export function drawSelection(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  definition: PartDefinition,
  instance: PartInstance,
): void {
  drawInstance(ctx, camera, definition, instance, {
    fill: 'rgba(255, 212, 121, 0.22)',
    stroke: '#ffd479',
    accent: '#ffd479',
    lineWidth: 2,
  });
}

/**
 * Rysuje cala konstrukcje prosto z blueprintu - bez fizyki. Uzywane w trybie
 * budowania, gdzie nie ma jeszcze swiata symulacji. Kola ida na wierzch.
 * `hiddenPartId` pomija klocek, ktory jest wlasnie przeciagany (rysowany jako
 * polprzezroczysty "duch" pod wskaznikiem) - zeby nie bylo go widac dwa razy.
 */
export function drawBlueprint(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  blueprint: RobotBlueprint,
  selectedPartId: string | null,
  hiddenPartId: string | null = null,
): void {
  const ordered = [...blueprint.parts]
    .filter((part) => part.id !== hiddenPartId)
    .sort((left, right) => {
      const rank = (part: PartInstance) => (getDefinition(part.typeId).wheel ? 1 : 0);
      return rank(left) - rank(right);
    });

  for (const instance of ordered) {
    drawInstance(ctx, camera, getDefinition(instance.typeId), instance);
  }

  const selected = ordered.find((part) => part.id === selectedPartId);
  if (selected) drawSelection(ctx, camera, getDefinition(selected.typeId), selected);
}